import { useMemo } from "react";
import type {
  Proposal,
  ResourceForecast,
  DashboardData,
} from "@/features/Dashboard";
import { SENIORITY_ORDER } from "@/features/Dashboard";

export function useDashboardLogic(proposals: Proposal[]) {
  const relevantProposals = useMemo(() => {
    const heatLevels: Proposal["heatLevel"][] = ["warm", "hot", "burning"];
    return proposals.filter((proposal) =>
      heatLevels.includes(proposal.heatLevel)
    );
  }, [proposals]);

  const resourceForecast = useMemo(() => {
    return relevantProposals.reduce((acc, proposal) => {
      proposal.resources.forEach((resource) => {
        const typeKey = resource.typeId;
        if (!acc[typeKey]) {
          acc[typeKey] = {
            typeId: resource.typeId,
            total: 0,
            proposals: new Set<string>(),
            seniorities: {
              Junior: { total: 0 },
              Pleno: { total: 0 },
              Senior: { total: 0 },
            },
            technologies: {},
          };
        }

        const seniorityKey =
          resource.seniority.charAt(0).toUpperCase() +
          resource.seniority.slice(1);
        const techKey = resource.technologyId;

        if (!acc[typeKey].technologies[techKey]) {
          acc[typeKey].technologies[techKey] = 0;
        }

        acc[typeKey].total += resource.quantity;
        acc[typeKey].proposals.add(proposal.name);

        if (acc[typeKey].seniorities[seniorityKey]) {
          acc[typeKey].seniorities[seniorityKey].total += resource.quantity;
        }

        acc[typeKey].technologies[techKey] += resource.quantity;
      });
      return acc;
    }, {} as Record<string, ResourceForecast>);
  }, [relevantProposals]);

  const totalResources = useMemo(() => {
    return Object.values(resourceForecast).reduce(
      (total, resource) => total + resource.total,
      0
    );
  }, [resourceForecast]);

  const seniorityTotals = useMemo(() => {
    return SENIORITY_ORDER.reduce((totals, seniority) => {
      totals[seniority] = Object.values(resourceForecast).reduce(
        (total, resource) =>
          total + (resource.seniorities[seniority]?.total || 0),
        0
      );
      return totals;
    }, {} as Record<string, number>);
  }, [resourceForecast]);

  const dashboardData: DashboardData = {
    totalResources,
    relevantProposals,
    resourceForecast,
    seniorityTotals,
  };

  return dashboardData;
}
